import { createFileRoute } from "@tanstack/react-router";
import { useAgent, useUpdateAgent } from "@/hooks/use-agents";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import {
  IconArrowLeft,
  IconLoader2,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";

const updateAgentSchema = z.object({
  agentName: z.string().min(2, "Name must be at least 2 characters").max(100, "Name must be less than 100 characters"),
  commissionRate: z.number().min(0, "Commission rate must be at least 0").max(100, "Commission rate must be less than 100"),
  status: z.enum(["active", "suspended"]),
});

type UpdateAgentForm = z.infer<typeof updateAgentSchema>;

export const Route = createFileRoute("/agents/$id/edit/")({
  component: AgentEditPage,
});

function AgentEditPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentEditContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentEditContent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: agent, isLoading } = useAgent(id);
  const updateAgent = useUpdateAgent(id);

  const form = useForm<UpdateAgentForm>({
    resolver: zodResolver(updateAgentSchema),
    defaultValues: {
      agentName: (agent as any)?.agentName || "",
      commissionRate: (agent as any)?.commissionRate || 0,
      status: (agent as any)?.status || "active",
    },
    values: {
      agentName: (agent as any)?.agentName || "",
      commissionRate: (agent as any)?.commissionRate || 0,
      status: (agent as any)?.status || "active",
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading agent information...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive font-medium">Agent not found</p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: UpdateAgentForm) => {
    try {
      await updateAgent.mutateAsync(data);
      toast.success("Agent updated successfully!");
      navigate({ to: "/agents/$id", params: { id } });
    } catch (error: any) {
      toast.error(error.message || "Failed to update agent");
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/agents/$id" params={{ id }}>
          <Button variant="ghost" size="sm">
            <IconArrowLeft className="h-4 w-4 mr-2" />
            Back to Agent
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edit Agent</h1>
          <p className="text-muted-foreground">Update agent information</p>
        </div>
      </div>

      {/* Edit Form */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Agent Information</CardTitle>
              <CardDescription>
                Update the agent's basic information and settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="agentName"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>Agent Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter agent name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="commissionRate"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>Commission Rate (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            placeholder="Enter commission rate"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="deleted">Deleted</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={updateAgent.isPending}
                      className="flex-1"
                    >
                      {updateAgent.isPending ? (
                        <>
                          <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        "Update Agent"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate({ to: "/agents/$id", params: { id } })}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Current Information</CardTitle>
              <CardDescription>
                Current agent details for reference
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Full Name</label>
                <p className="text-sm text-muted-foreground">{(agent as any)?.agentName}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <p className="text-sm text-muted-foreground">{(agent as any)?.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Username</label>
                <p className="text-sm text-muted-foreground">{(agent as any)?.username}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Serial Number</label>
                <p className="text-sm text-muted-foreground">{(agent as any)?.serialNumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Current Status</label>
                <p className="text-sm text-muted-foreground capitalize">{(agent as any)?.status}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Current Commission</label>
                <p className="text-sm text-muted-foreground">{(agent as any)?.commissionRate}%</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Related actions for this agent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/agents/$id" params={{ id }}>
                <Button variant="outline" className="w-full">
                  View Details
                </Button>
              </Link>
              <Link to="/agents/$id/sims" params={{ id }}>
                <Button variant="outline" className="w-full">
                  Manage SIMs
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}